import { useState, useEffect, useRef, ReactNode } from 'react'
import { MoreVertical } from 'lucide-react'
import { createPortal } from 'react-dom'
import { cn } from '../../utils/cn'

export type ActionMenuItem = {
  label: string
  icon?: ReactNode
  onClick: () => void
  variant?: 'default' | 'danger' | 'warning'
  /** renders a divider line above this item */
  divider?: boolean
  disabled?: boolean
  title?: string
}

const MENU_WIDTH = 192
const MENU_HEIGHT = 200

export function ActionMenu({ items }: { items: ActionMenuItem[] }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number }>({ top: 0, left: 0 })
  const [openUpward, setOpenUpward] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)

  function handleOpen(e: React.MouseEvent) {
    e.stopPropagation()
    if (!btnRef.current) return
    const rect = btnRef.current.getBoundingClientRect()
    // The kebab is the row's LEFT-most cell, so the menu opens rightward: its left edge is
    // aligned to the button's left. Clamped so it never runs off the right edge on narrow
    // viewports (and never past the left gutter).
    const rawLeft = rect.left
    const safeLeft = Math.max(10, Math.min(rawLeft, window.innerWidth - MENU_WIDTH - 10))
    const spaceBelow = window.innerHeight - rect.bottom

    if (spaceBelow < MENU_HEIGHT) {
      setPos({ bottom: window.innerHeight - rect.top + 4, left: safeLeft })
      setOpenUpward(true)
    } else {
      setPos({ top: rect.bottom + 4, left: safeLeft })
      setOpenUpward(false)
    }
    setOpen(o => !o)
  }

  useEffect(() => {
    if (!open) return
    function close() { setOpen(false) }
    document.addEventListener('click', close)
    window.addEventListener('scroll', close, true)
    return () => {
      document.removeEventListener('click', close)
      window.removeEventListener('scroll', close, true)
    }
  }, [open])

  const dropdownStyle = openUpward
    ? { position: 'fixed' as const, bottom: pos.bottom, left: pos.left, zIndex: 9999 }
    : { position: 'fixed' as const, top: pos.top, left: pos.left, zIndex: 9999 }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={handleOpen}
        className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-400"
        aria-label="Actions"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {open && createPortal(
        <div
          style={dropdownStyle}
          className="bg-white rounded-xl shadow-xl border border-gray-100 py-1 w-48"
          onClick={e => e.stopPropagation()}
        >
          {items.map((item, i) => (
            <div key={i}>
              {item.divider && <div className="border-t border-gray-100 my-1" />}
              <button
                type="button"
                onClick={() => {
                  if (item.disabled) return
                  setOpen(false)
                  item.onClick()
                }}
                disabled={item.disabled}
                title={item.title}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors',
                  item.disabled
                    ? 'text-gray-300 cursor-not-allowed'
                    : item.variant === 'danger'  ? 'text-red-600   hover:bg-red-50'
                    : item.variant === 'warning' ? 'text-amber-600 hover:bg-amber-50'
                    :                              'text-gray-700  hover:bg-gray-50',
                )}
              >
                {item.icon && (
                  <span className="flex-shrink-0 text-current">{item.icon}</span>
                )}
                {item.label}
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </>
  )
}
